import type { TicketKalender, TicketSpalte, TicketZeile } from "@kundeninfo/api-client";
import { istWochenendTag } from "@kundeninfo/ui";

/** Beschriftungen und Zuordnungen der Tickets-Oberfläche — reine Funktionen, ohne Kalenderregeln
 *  (die kommen als Liste vom Server, F18). */

const WOCHENTAG = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"] as const;
const MONAT = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"] as const;

function teile(tag: string): [number, number, number] {
  const [j, m, t] = tag.split("-").map(Number);
  return [j!, m!, t!];
}
const zwei = (n: number): string => String(n).padStart(2, "0");

function wochentag(tag: string): string {
  const [j, m, t] = teile(tag);
  return WOCHENTAG[new Date(Date.UTC(j, m - 1, t)).getUTCDay()]!;
}

export function tagBeschriftung(tag: string): string {
  const [, m, t] = teile(tag);
  return `${wochentag(tag)} ${zwei(t)}.${zwei(m)}.`;
}

export function heuteBeschriftung(tag: string): string {
  const [j, m, t] = teile(tag);
  return `${wochentag(tag)} ${zwei(t)}.${zwei(m)}.${j}`;
}

export function monatBeschriftung(von: string): string {
  const [j, m, t] = teile(von);
  const basis = `${MONAT[m - 1]} ${j}`;
  return t === 1 ? basis : `${basis} ab ${t}.`;
}

export function zeilenText(z: TicketZeile): string {
  if (z.art === "JETZT") return "Jetzt · fällig";
  if (z.art === "TAG") return tagBeschriftung(z.datum!);
  if (z.art === "MONAT") return monatBeschriftung(z.von!);
  return "später";
}

/** Wohin eine Ablage auf Zeile oder Spaltenkopf führt; `text` steht im Ablage-Hinweis. */
export type AblageZiel = {
  columnId: string; ziel: "JETZT" | "TAG" | "MONAT" | "SPAETER" | "KOPF"; datum: string | null; text: string;
};

/** Das Ablageziel einer Zeitleisten-Zeile (auch für den Ablage-Hinweis der Folgeaufgaben). */
export function zielVon(spalte: TicketSpalte, z: TicketZeile): AblageZiel {
  const text = `${spalte.name} · ${zeilenText(z).replace(" · fällig", "")}`;
  if (z.art === "JETZT") return { columnId: spalte.id, ziel: "JETZT", datum: null, text };
  if (z.art === "TAG") return { columnId: spalte.id, ziel: "TAG", datum: z.datum!, text };
  return { columnId: spalte.id, ziel: z.art, datum: z.von!, text };
}

export function tageZwischen(von: string, bis: string): number {
  const [a, b, c] = teile(von);
  const [x, y, z] = teile(bis);
  return Math.round((Date.UTC(x, y - 1, z) - Date.UTC(a, b - 1, c)) / 86_400_000);
}

export function datumsMarke(resubmitDate: string | null, heute: string):
    { text: string; art: "fehler" | "warnung" | null; ohne: boolean } {
  if (resubmitDate === null) return { text: "ohne Datum", art: null, ohne: true };
  if (resubmitDate < heute) {
    return { text: `${tagBeschriftung(resubmitDate)} · ${tageZwischen(resubmitDate, heute)} T`, art: "fehler", ohne: false };
  }
  if (resubmitDate === heute) return { text: "heute", art: "warnung", ohne: false };
  return { text: tagBeschriftung(resubmitDate), art: null, ohne: false };
}

/** Die Zeile eines Tickets aus den vom Server gelieferten Grenzen (kein eigener Kalender). */
export function zeileFuer(resubmitDate: string | null, zeilen: readonly TicketZeile[], heute: string): number {
  if (resubmitDate === null || resubmitDate <= heute) return 0;
  for (const z of zeilen) {
    if (z.art === "TAG" && z.datum === resubmitDate) return z.index;
    if (z.art === "MONAT" && resubmitDate >= z.von! && resubmitDate <= z.bis!) return z.index;
  }
  return zeilen[zeilen.length - 1]?.index ?? 0;
}

export function freieTage(kalender: readonly TicketKalender[]): Map<string, { name: string; kuerzel: string[] }> {
  const tage = new Map<string, { name: string; kuerzel: string[] }>();
  for (const k of kalender) {
    for (const f of k.feiertage) {
      const da = tage.get(f.datum);
      if (da === undefined) tage.set(f.datum, { name: f.name, kuerzel: [k.shortCode] });
      else da.kuerzel.push(k.shortCode);
    }
  }
  return tage;
}

export function alleFeiertage(kalender: readonly TicketKalender[]): string[] {
  return [...freieTage(kalender).keys()];
}

/** „Wochenende", „Feiertag: X" und — wenn nicht alle gezeigten Unternehmen frei haben — die
 *  Kürzel (132 vorläufig). Leer = kein Popup. */
export function popupFuerTag(tag: string, kalender: readonly TicketKalender[]): string {
  const teileText: string[] = [];
  if (istWochenendTag(tag)) teileText.push("Wochenende");
  const f = freieTage(kalender).get(tag);
  if (f !== undefined) {
    teileText.push(f.kuerzel.length < kalender.length
      ? `Feiertag: ${f.name} · ${f.kuerzel.join(", ")}` : `Feiertag: ${f.name}`);
  }
  return teileText.join(" · ");
}

export function feiertageImBereich(von: string, bis: string, kalender: readonly TicketKalender[]):
    { datum: string; name: string; kuerzel: string[] }[] {
  return [...freieTage(kalender).entries()]
    .filter(([d]) => d >= von && d <= bis)
    .map(([datum, f]) => ({ datum, ...f }))
    .sort((a, b) => a.datum.localeCompare(b.datum));
}

/** Die Zahl der Feiertage einer Monatszeile als Beschriftung (Zeitleiste und Raster). */
export function feiertageText(anzahl: number): string {
  return anzahl === 1 ? "1 Feiertag" : `${anzahl} Feiertage`;
}

/** Initialen aus Vor- und Nachname (erstes und letztes Wort), F48. */
export function kuerzelVon(name: string): string {
  const w = name.split(/\s+/).filter((x) => x !== "");
  if (w.length === 0) return "?";
  return (w.length === 1 ? w[0]![0]! : `${w[0]![0]!}${w[w.length - 1]![0]!}`).toUpperCase();
}

const BERLINER_TAG = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin", year: "numeric",
  month: "2-digit", day: "2-digit" });

/** Der Berliner Kalendertag eines Zeitpunkts (F13) — nicht der UTC-Tag aus `iso.slice(0, 10)`. */
export function berlinerTag(iso: string): string {
  return BERLINER_TAG.format(new Date(iso));
}

const BERLINER_ZEITPUNKT = new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", day: "2-digit",
  month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

/** Ein Zeitpunkt als „TT.MM.JJJJ HH:MM" in Europe/Berlin (F13) — etwa „Stand von …" einer Verknüpfung. */
export function berlinerZeitpunkt(iso: string): string {
  return BERLINER_ZEITPUNKT.format(new Date(iso)).replace(",", "");
}
